import React from 'react';
import { Chip } from '@jotpsych/ui';
import {
  alpha,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { CleanupTypeVersion } from '../types';
import { lastUsedLabel } from '../utils/versionSignals';

export interface SetAsideItem {
  version: CleanupTypeVersion;
  /** Display name of the group it was set aside from. */
  fromGroup: string;
}

export interface SetAsideRenamesProps {
  items: SetAsideItem[];
  /** Effective new name per version id (defaulted by the parent). */
  renames: Readonly<Record<string, string>>;
  onRenameChange: (versionId: string, name: string) => void;
  /** Set-asides the admin chose to archive instead of rename. */
  archivedIds?: ReadonlySet<string>;
  onToggleArchive?: (versionId: string) => void;
  disabled?: boolean;
}

/**
 * Loose-ends section for template outliers the admin set aside in the
 * merge lanes: they kept their template and stayed personal, but their
 * name still collides with the shared type they were excluded from —
 * rename them to what they actually are. Pure presentation.
 */
export const SetAsideRenames: React.FC<SetAsideRenamesProps> = ({
  items,
  renames,
  onRenameChange,
  archivedIds = new Set<string>(),
  onToggleArchive,
  disabled = false,
}) => {
  if (items.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Set aside for renaming
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 680 }}
      >
        You kept these out of a merge because they point at a different
        template — they&apos;re probably misnamed, not duplicates. Give each
        the name it actually deserves so it stops colliding with the shared
        type (we&apos;ve suggested the template&apos;s name) — or archive it
        if it&apos;s just dead.
      </Typography>

      <Stack spacing={1} sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
        {items.map(({ version, fromGroup }) => {
          const value = renames[version.id] ?? '';
          const archived = archivedIds.has(version.id);
          return (
            <Box
              key={version.id}
              sx={(theme) => ({
                border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.03),
                p: 1.25,
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                flexWrap: 'wrap',
                opacity: archived ? 0.55 : 1,
              })}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textDecoration: archived ? 'line-through' : 'none',
                    }}
                  >
                    {version.provider ?? 'Unassigned'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    &ldquo;{version.name}&rdquo; · {version.usage} visits
                    {version.lastUsedAt &&
                      ` · ${lastUsedLabel(version.lastUsedAt)}`}
                    {version.name.trim() !== fromGroup.trim() &&
                      ` · set aside from "${fromGroup}"`}
                  </Typography>
                  {version.templateTitle ? (
                    <Chip
                      size="xSmall"
                      variant="soft"
                      color="success"
                      label={`template: ${version.templateTitle}`}
                    />
                  ) : (
                    <Chip
                      size="xSmall"
                      variant="soft"
                      color="warning"
                      label="no template set"
                    />
                  )}
                </Stack>
              </Box>
              {archived ? (
                <Chip
                  size="xSmall"
                  variant="soft"
                  color="warning"
                  label="will archive"
                />
              ) : (
                <TextField
                  size="small"
                  label="Rename to"
                  value={value}
                  disabled={disabled}
                  onChange={(e) => onRenameChange(version.id, e.target.value)}
                  sx={{ minWidth: 260, flexShrink: 0 }}
                  helperText={
                    value.trim() === version.name.trim()
                      ? version.templateTitle
                        ? 'Same name — the collision with the shared type stays'
                        : 'No template to suggest a name from — type what ' +
                          'this visit actually is'
                      : undefined
                  }
                />
              )}
              {onToggleArchive && (
                <Button
                  size="small"
                  variant="text"
                  color={archived ? 'primary' : 'inherit'}
                  disabled={disabled}
                  onClick={() => onToggleArchive(version.id)}
                  sx={{ flexShrink: 0 }}
                >
                  {archived ? 'Restore' : 'Archive'}
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
